import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { toast } from "sonner";
import { fr } from "date-fns/locale";
import { format } from "date-fns";
import { Calendar as CalIcon, Mail, Phone, MapPin, CheckCircle2, Sparkles, ChevronRight, Loader2, Tent, QrCode } from "lucide-react";
import { MiniSiteReviews, MiniSiteRatingBadge } from "@/components/public/MiniSiteReviews";
import { KeyRound, MessagesSquare } from "lucide-react";
import { ClientAccessDialog } from "@/components/modules/ClientAccessDialog";
import { moduleIcon } from "@/components/modules/moduleUtils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublicMiniSite() {
  const [accessOpen, setAccessOpen] = useState(false);
  const { code } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  const [selectedOffering, setSelectedOffering] = useState(null);
  const [bookingOpen, setBookingOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({ client_nom: "", client_email: "", client_telephone: "", date: null, time: "", message: "" });

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/public/sites/${code}`);
        setData(r.data);
      } catch (e) {
        setError(e?.response?.data?.detail || "Mini-site indisponible.");
      } finally {
        setLoading(false);
      }
    })();
  }, [code]);

  const openBook = (off) => {
    setSelectedOffering(off);
    setForm({ client_nom: "", client_email: "", client_telephone: "", date: null, time: "", message: "" });
    setDone(false);
    setBookingOpen(true);
  };

  const buildDateSouhaitee = () => {
    if (!form.date) return null;
    const d = new Date(form.date);
    if (form.time) {
      const [hh, mm] = form.time.split(":").map((x) => parseInt(x, 10) || 0);
      d.setHours(hh, mm, 0, 0);
    } else {
      d.setHours(12, 0, 0, 0);
    }
    return d.toISOString();
  };

  const submitBooking = async (e) => {
    e.preventDefault();
    if (!form.client_nom) return toast.error("Votre nom est requis");
    setSubmitting(true);
    try {
      await axios.post(`${API}/public/sites/${code}/book`, {
        client_nom: form.client_nom,
        client_email: form.client_email,
        client_telephone: form.client_telephone,
        date_souhaitee: buildDateSouhaitee(),
        message: form.message,
        offering_label: selectedOffering?.label || "",
      });
      setDone(true);
      toast.success("Votre demande a été envoyée");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    } finally {
      setSubmitting(false);
    }
  };

  const submitPay = async () => {
    if (!form.client_nom || !form.client_email) return toast.error("Nom et email requis pour le paiement");
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/public/sites/${code}/checkout`, {
        offering_label: selectedOffering.label,
        client_nom: form.client_nom,
        client_email: form.client_email,
        origin_url: window.location.origin,
      });
      try {
        await axios.post(`${API}/public/sites/${code}/book`, {
          client_nom: form.client_nom,
          client_email: form.client_email,
          client_telephone: form.client_telephone,
          date_souhaitee: buildDateSouhaitee(),
          message: form.message,
          offering_label: selectedOffering.label,
        });
      } catch { /* non bloquant */ }
      window.location.href = r.data.url;
    } catch (err) {
      setSubmitting(false);
      toast.error(err?.response?.data?.detail || "Paiement indisponible");
    }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8F9FA]">
      <Loader2 className="h-6 w-6 animate-spin text-[#002FA7]" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-[#F8F9FA] p-6">
      <div className="max-w-md text-center">
        <div className="text-7xl font-display font-black tracking-tight text-[#002FA7]">404</div>
        <div className="mt-3 text-sm uppercase tracking-[0.25em] font-bold">{error}</div>
        <Button onClick={() => navigate("/")} className="mt-6 bg-[#002FA7] text-white h-11">Retour à PAG</Button>
      </div>
    </div>
  );

  const cfg = data.config;
  const ent = data.entreprise;
  const accent = cfg.color || "#002FA7";
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const logoSrc = ent.logo_path ? `${BACKEND_URL}/api/files/${ent.logo_path}` : null;

  return (
    <div className="min-h-screen bg-white" data-testid="public-minisite">
      <ClientAccessDialog open={accessOpen} onOpenChange={setAccessOpen} postUrl={`/public/sites/${code}/client-access`} entrepriseName={ent.name} />
      {data.preview && (
        <div className="bg-[#FFFBEB] border-b border-[#FFD400]/40 text-[11px] uppercase tracking-[0.25em] font-bold text-[#D97706] text-center py-2" data-testid="preview-banner">
          ⚠ Aperçu — ce mini-site est en cours d'activation, les réservations resteront en attente.
        </div>
      )}
      {/* Phase 83 — lien vers le Site web Pro relié */}
      {data.pro_site && (
        <a href={data.pro_site.url} className="block bg-[#002FA7] text-white text-center text-xs font-bold uppercase tracking-[0.2em] py-2.5 hover:bg-[#001f70] transition-colors" data-testid="minisite-pro-site-link">
          Découvrez notre site complet : {data.pro_site.name} →
        </a>
      )}
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-[#DEE2E6]" style={{ background: `linear-gradient(135deg, ${accent} 0%, #000 140%)` }}>
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-16 lg:py-24 text-white">
          <div className="flex items-center gap-3 text-xs uppercase tracking-[0.3em] font-bold opacity-80">
            <Sparkles className="h-3 w-3" /> {ent.secteur || "Service public"}
          </div>
          <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[0.95]">
            {ent.name}
          </h1>
          {cfg.intro && (
            <p className="mt-6 max-w-2xl text-base lg:text-lg text-white/90 leading-relaxed">
              {cfg.intro}
            </p>
          )}
          <div className="mt-6"><MiniSiteRatingBadge code={code} /></div>
          <div className="mt-8 flex flex-wrap gap-4 text-xs uppercase tracking-[0.2em] font-semibold">
            {cfg.contact_email && <a href={`mailto:${cfg.contact_email}`} className="inline-flex items-center gap-2 hover:underline"><Mail className="h-3 w-3" /> {cfg.contact_email}</a>}
            {cfg.contact_telephone && <a href={`tel:${cfg.contact_telephone}`} className="inline-flex items-center gap-2 hover:underline"><Phone className="h-3 w-3" /> {cfg.contact_telephone}</a>}
            {ent.adresse && <span className="inline-flex items-center gap-2 opacity-80"><MapPin className="h-3 w-3" /> {ent.adresse}</span>}
            <button type="button" onClick={() => setAccessOpen(true)} className="inline-flex items-center gap-2 border border-white/40 px-3 py-1.5 hover:bg-white hover:text-black transition-colors" data-testid="minisite-client-access"><KeyRound className="h-3 w-3" /> Espace client</button>
          </div>
          {logoSrc && (
            <img src={logoSrc} alt={ent.name} className="absolute right-6 top-6 h-16 w-16 object-contain bg-white p-2 lg:right-10 lg:top-10" />
          )}
        </div>
      </section>

      {/* Réservation d'hébergement */}
      {data.reservation?.available && (
        <section className="border-b border-[#DEE2E6] bg-[#0A0A0A] text-white" data-testid="minisite-reservation-cta">
          <div className="max-w-6xl mx-auto px-6 lg:px-10 py-10 flex flex-wrap items-center gap-6">
            <div className="flex-1 min-w-[260px]">
              <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#FFD700] flex items-center gap-2">
                <Tent className="h-3 w-3" />{data.reservation.labels?.units || "Hébergements"}
              </div>
              <h2 className="mt-2 font-display text-3xl lg:text-4xl font-black tracking-tight">
                Réserver votre séjour
              </h2>
              <p className="mt-2 text-sm text-white/70 max-w-xl">
                Choisissez vos dates et votre {(data.reservation.labels?.unit || "hébergement").toLowerCase()}.
                Vous recevez votre QR code d'entrée par email — arrivée dès {data.reservation.checkin_time},
                départ avant {data.reservation.checkout_time}.
              </p>
            </div>
            <div className="flex flex-col gap-2">
              <Button onClick={() => navigate(`/sites/${code}/reservation`)} data-testid="minisite-reserve-btn"
                      className="h-14 px-8 bg-[#FFD700] hover:bg-[#E6C200] text-black font-black text-base">
                Réserver en ligne <ChevronRight className="h-5 w-5 ml-1" />
              </Button>
              <button onClick={() => navigate(`/sites/${code}/reservation`)} data-testid="minisite-find-qr"
                      className="text-[11px] uppercase tracking-[0.2em] text-white/50 hover:text-white flex items-center gap-1 justify-center">
                <QrCode className="h-3 w-3" />Retrouver mon QR code
              </button>
            </div>
          </div>
        </section>
      )}

      {/* Phase 90 — Demandes en ligne (modules ouverts aux clients) */}
      {data.module_forms?.length > 0 && (
        <section className="border-b border-[#DEE2E6] bg-[#F8F9FA]" data-testid="minisite-module-forms">
          <div className="max-w-6xl mx-auto px-6 lg:px-10 py-12">
            <div className="flex items-end justify-between gap-4 mb-6">
              <div>
                <div className="text-xs uppercase tracking-[0.3em] font-bold text-[#868E96] flex items-center gap-2"><MessagesSquare className="h-3.5 w-3.5" />Demandes en ligne</div>
                <h2 className="mt-2 font-display text-2xl lg:text-3xl font-black tracking-tight">Faites votre demande, suivez-la dans votre espace client</h2>
              </div>
              <button type="button" onClick={() => setAccessOpen(true)} className="hidden sm:inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#002FA7] hover:underline" data-testid="minisite-module-forms-access"><KeyRound className="h-3.5 w-3.5" />Déjà client ?</button>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {data.module_forms.map((f) => { const Icon = moduleIcon(f.icon); return (
                <button type="button" key={f.ref} onClick={() => navigate(`/sites/${code}/demande/${f.module_key}/${f.entity_key}`)} className="text-left bg-white border border-[#DEE2E6] hover:border-[#0A0A0A] p-5 flex items-start gap-3 transition-colors" data-testid={`minisite-module-form-${f.ref}`}>
                  <span className="h-10 w-10 flex items-center justify-center shrink-0 text-white" style={{ background: accent }}><Icon className="h-4 w-4" /></span>
                  <span className="min-w-0 flex-1">
                    <span className="block font-display font-bold text-base leading-tight">{f.button_label}</span>
                    <span className="block text-xs text-[#495057] mt-1 line-clamp-2">{f.intro || f.description || `${f.label} · ${f.module_name}`}</span>
                    <span className="mt-2 inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider text-[#002FA7]">Commencer <ChevronRight className="h-3 w-3" /></span>
                  </span>
                </button>
              ); })}
            </div>
          </div>
        </section>
      )}

      {/* Photo */}
      {cfg.photo_url && (
        <div className="max-w-6xl mx-auto px-6 lg:px-10 pt-10">
          <img src={cfg.photo_url} alt="" className="w-full h-72 lg:h-[420px] object-cover border border-[#DEE2E6]" />
        </div>
      )}

      {/* Offerings */}
      <section className="max-w-6xl mx-auto px-6 lg:px-10 py-16">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] font-bold text-[#868E96]">Nos prestations</div>
            <h2 className="mt-2 font-display text-3xl lg:text-4xl font-black tracking-tight">Réservez en quelques secondes</h2>
          </div>
        </div>

        {(!cfg.offerings || cfg.offerings.length === 0) ? (
          <div className="bg-[#F8F9FA] border border-[#DEE2E6] p-10 text-center text-sm text-[#868E96]" data-testid="no-offerings">
            Les prestations seront bientôt disponibles. Contactez-nous directement par email ou téléphone.
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="offerings-grid">
            {cfg.offerings.map((o, i) => (
              <article key={i} className="border border-[#DEE2E6] bg-white p-6 flex flex-col group hover:shadow-lg transition-shadow" data-testid={`offering-${i}`}>
                <div className="text-xs uppercase tracking-[0.25em] font-bold" style={{ color: accent }}>Prestation {String(i + 1).padStart(2, "0")}</div>
                <h3 className="mt-2 font-display text-xl font-black tracking-tight">{o.label}</h3>
                {o.description && <p className="mt-2 text-sm text-[#495057] flex-1">{o.description}</p>}
                <div className="mt-5 flex items-end justify-between">
                  <div>
                    {o.duree_min && <div className="text-[10px] uppercase tracking-wider text-[#868E96]">Durée</div>}
                    {o.duree_min && <div className="font-mono font-bold">{o.duree_min} min</div>}
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] uppercase tracking-wider text-[#868E96]">Prix</div>
                    <div className="font-display font-black text-2xl">{(o.prix > 0) ? `${o.prix.toFixed(2)} €` : "Gratuit"}</div>
                  </div>
                </div>
                <Button
                  onClick={() => openBook(o)}
                  data-testid={`book-btn-${i}`}
                  className="mt-5 h-11 w-full font-semibold text-white"
                  style={{ background: accent }}
                >
                  {o.prix > 0 ? "Réserver et payer" : "Réserver"} <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </article>
            ))}
          </div>
        )}
      </section>

      <MiniSiteReviews code={code} accent={accent} />

      {/* Établissements (multi-sites) */}
      {data.etablissements?.length > 0 && (
        <section className="border-t border-[#DEE2E6] bg-[#F8F9FA]" data-testid="minisite-etablissements">
          <div className="max-w-6xl mx-auto px-6 lg:px-10 py-14">
            <div className="text-xs uppercase tracking-[0.3em] font-bold text-[#868E96]">Nos établissements</div>
            <h2 className="mt-2 font-display text-2xl lg:text-3xl font-black tracking-tight">{data.etablissements.length} adresse{data.etablissements.length > 1 ? "s" : ""} pour vous accueillir</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mt-8">
              {data.etablissements.map((e) => (
                <article key={e.id} className="bg-white border border-[#DEE2E6] p-5" data-testid={`minisite-etab-${e.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="font-display text-lg font-black tracking-tight">{e.name}</h3>
                    {e.is_siege && <span className="text-[9px] uppercase tracking-wider font-bold text-white px-1.5 py-0.5" style={{ background: accent }}>Siège</span>}
                  </div>
                  {(e.adresse || e.ville) && (
                    <div className="mt-2 text-sm text-[#495057] flex items-start gap-1.5"><MapPin className="h-3.5 w-3.5 mt-0.5 shrink-0" /><span>{e.adresse}{e.adresse && (e.code_postal || e.ville) ? ", " : ""}{[e.code_postal, e.ville].filter(Boolean).join(" ")}</span></div>
                  )}
                  {e.horaires && <div className="mt-1 text-sm text-[#495057] flex items-start gap-1.5"><CalIcon className="h-3.5 w-3.5 mt-0.5 shrink-0" /><span>{e.horaires}</span></div>}
                  <div className="mt-3 flex flex-wrap gap-3 text-xs font-semibold">
                    {e.telephone && <a href={`tel:${e.telephone}`} className="inline-flex items-center gap-1 hover:underline" style={{ color: accent }}><Phone className="h-3 w-3" /> {e.telephone}</a>}
                    {e.email && <a href={`mailto:${e.email}`} className="inline-flex items-center gap-1 hover:underline" style={{ color: accent }}><Mail className="h-3 w-3" /> {e.email}</a>}
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="border-t border-[#DEE2E6] py-8 text-center text-[10px] uppercase tracking-[0.25em] font-bold text-[#868E96]">
        Propulsé par <a href="/" className="text-[#002FA7] hover:underline">PAG Business Manager</a> · {ent.code}
      </footer>

      {/* Booking dialog */}
      <Dialog open={bookingOpen} onOpenChange={(o) => { setBookingOpen(o); if (!o) setDone(false); }}>
        <DialogContent className="max-w-lg" data-testid="booking-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-black tracking-tight">
              {done ? "Demande envoyée" : (selectedOffering?.label || "Réservation")}
            </DialogTitle>
          </DialogHeader>
          {done ? (
            <div className="py-6 text-center">
              <CheckCircle2 className="h-14 w-14 mx-auto" style={{ color: accent }} />
              <div className="mt-4 font-semibold">Merci {form.client_nom} !</div>
              <p className="mt-2 text-sm text-[#495057]">Votre demande a été transmise à {ent.name}. Vous serez recontacté(e) au plus vite.</p>
              <Button onClick={() => setBookingOpen(false)} data-testid="booking-close" className="mt-6 h-11 bg-[#002FA7] text-white">Fermer</Button>
            </div>
          ) : (
            <form onSubmit={submitBooking} className="space-y-3" data-testid="booking-form">
              <div className="grid grid-cols-2 gap-3">
                <Fld label="Nom complet *" v={form.client_nom} on={(v) => setForm({ ...form, client_nom: v })} required testid="bk-nom" />
                <Fld label="Email" type="email" v={form.client_email} on={(v) => setForm({ ...form, client_email: v })} testid="bk-email" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Fld label="Téléphone" v={form.client_telephone} on={(v) => setForm({ ...form, client_telephone: v })} testid="bk-tel" />
                <div>
                  <Label className="text-xs uppercase tracking-[0.2em] font-semibold">Heure</Label>
                  <Input type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} className="h-11 mt-1" data-testid="bk-time" />
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.2em] font-semibold">Date souhaitée</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button type="button" variant="outline" data-testid="bk-date" className="h-11 mt-1 w-full justify-start font-normal">
                      <CalIcon className="h-4 w-4 mr-2 text-[#002FA7]" />
                      {form.date ? format(new Date(form.date), "EEEE d MMMM yyyy", { locale: fr }) : <span className="text-[#868E96]">Choisir une date</span>}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={form.date ? new Date(form.date) : undefined}
                      onSelect={(d) => setForm({ ...form, date: d ? d.toISOString() : null })}
                      locale={fr}
                      disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.2em] font-semibold">Message</Label>
                <Textarea rows={3} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-1" data-testid="bk-msg" placeholder="Précisions éventuelles…" />
              </div>
              <div className="border-t border-[#DEE2E6] pt-3 flex items-center justify-between text-xs">
                <span className="uppercase tracking-wider font-bold text-[#868E96]">Prix</span>
                <span className="font-display font-black text-xl">{(selectedOffering?.prix > 0) ? `${selectedOffering.prix.toFixed(2)} €` : "Gratuit"}</span>
              </div>
              <DialogFooter className="gap-2">
                <Button type="submit" disabled={submitting} variant="outline" data-testid="bk-submit-free">
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Envoyer la demande"}
                </Button>
                {selectedOffering?.prix > 0 && (
                  <Button type="button" disabled={submitting} onClick={submitPay} className="text-white" style={{ background: accent }} data-testid="bk-submit-pay">
                    {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : `Payer ${selectedOffering.prix.toFixed(2)} €`}
                  </Button>
                )}
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Fld({ label, v, on, type = "text", required, testid }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-[0.2em] font-semibold">{label}</Label>
      <Input data-testid={testid} type={type} value={v} onChange={(e) => on(e.target.value)} required={required} className="h-11 mt-1" />
    </div>
  );
}
